<template>
  <div class="flex flex-col h-full bg-slate-50 overflow-hidden">
    <div class="flex items-center justify-between p-4 bg-white border-b border-slate-200">
      <div class="flex items-center space-x-4">
        <div class="flex -space-x-2">
          <template v-for="tech in technicians">
            <div :key="tech.id" :title="tech.name" 
                 :style="{ backgroundColor: tech.color }"
                 class="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[10px] text-white font-bold cursor-help">
              {{ tech.name.charAt(0) }}
            </div>
          </template>
        </div>
        <span class="h-6 w-px bg-slate-200"></span>
        <div class="flex bg-slate-100 p-1 rounded-lg">
          <button class="px-3 py-1 text-xs font-medium bg-white shadow-sm rounded-md text-slate-700">Vue Chronologique</button>
          <button class="px-3 py-1 text-xs font-medium text-slate-500 hover:text-slate-700">Vue Liste</button>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <span class="flex items-center text-xs font-medium text-slate-500">
          <span class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse mr-2"></span>
          {{ liveInterventionsCount }} interventions en cours
        </span>
        <button @click="optimizeRoutes" class="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all shadow-md">
          <i class="fa-solid fa-wand-magic-sparkles mr-2"></i> RÉOPTIMISER LA JOURNÉE
        </button>
      </div>
    </div>

    <div class="flex-1 flex flex-col min-h-0">
      <div class="h-1/3 min-h-[300px] border-b border-slate-300 relative">
        <InterventionMapTournee :addresses="addresses" class="h-full w-full" />
        
        <div class="absolute top-4 right-4 bg-white/90 backdrop-blur shadow-xl border border-slate-200 rounded-lg p-3 text-[11px] space-y-2">
          <div class="flex items-center text-slate-600 font-bold uppercase tracking-wider pb-1 border-b">Légende</div>
          <div class="flex items-center"><i class="fa-solid fa-bolt text-red-500 w-4"></i> Dépannage Urgent</div>
          <div class="flex items-center"><i class="fa-solid fa-screwdriver-wrench text-blue-500 w-4"></i> Maintenance</div>
          <div class="flex items-center"><i class="fa-solid fa-box text-emerald-500 w-4"></i> Pose / Installation</div>
        </div>
      </div>

      <div class="flex-1 overflow-auto bg-slate-50 p-4">
        <div class="min-w-[1000px] bg-white rounded-xl border border-slate-200 shadow-sm relative">
          
          <div class="absolute top-0 bottom-0 w-px bg-red-500 z-30 shadow-[0_0_8px_rgba(239,68,68,0.5)]" 
               :style="{ left: currentTimePosition }">
            <div class="bg-red-500 text-white text-[9px] px-1 rounded-sm absolute -top-2 -left-6 font-bold">LIVE</div>
          </div>

          <div class="flex border-b border-slate-100 bg-slate-50/50 rounded-t-xl">
            <div class="w-72 p-4 border-r border-slate-200 font-bold text-slate-400 text-[10px] uppercase tracking-widest text-center">Ressources</div>
            <div class="flex-1 flex">
              <div v-for="h in hours" :key="h" class="flex-1 py-3 text-center text-[10px] font-bold text-slate-400 border-l border-slate-100/50">
                {{ h }}:00
              </div>
            </div>
          </div>

          <div v-for="tech in technicians" :key="tech.id" class="flex border-b border-slate-50 group hover:bg-slate-50/80 transition-colors">
            
            <div class="w-72 p-4 border-r border-slate-200 flex flex-col justify-center">
              <div class="flex items-center mb-2">
                <div class="relative">
                  <div class="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg font-bold" :style="{ backgroundColor: tech.color }">
                    {{ tech.name.split(' ').map(n => n[0]).join('') }}
                  </div>
                  <span :class="['absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white', tech.online ? 'bg-emerald-500' : 'bg-slate-300']"></span>
                </div>
                <div class="ml-3 overflow-hidden">
                  <h4 class="font-bold text-sm text-slate-800 truncate">{{ tech.name }}</h4>
                  <div class="text-[10px] text-slate-500 flex items-center italic">
                    <i class="fa-solid fa-location-dot mr-1"></i> {{ tech.lastLocation || 'Lyon Centre' }}
                  </div>
                </div>
              </div>
              <div class="w-full bg-slate-100 h-1 rounded-full overflow-hidden flex">
                <div class="bg-emerald-400 h-full" :style="{ width: tech.progress + '%' }"></div>
              </div>
              <div class="flex justify-between mt-1 text-[9px] font-bold text-slate-400 uppercase">
                <span>Charge</span>
                <span>{{ tech.progress }}%</span>
              </div>
            </div>

            <div class="flex-1 relative h-28 px-2 flex items-center">
              <div class="absolute inset-0 flex pointer-events-none">
                <div v-for="n in 10" :key="n" class="flex-1 border-r border-slate-100/30"></div>
              </div>

              <div 
                v-for="inter in getInterventionsByTech(tech.id)" 
                :key="inter.id"
                @click="openDetails(inter)"
                :class="[
                  'absolute h-16 rounded-xl shadow-sm border p-2 cursor-pointer transition-all z-10 flex flex-col justify-between group/card overflow-hidden',
                  getStatusClass(inter)
                ]"
                :style="calculatePosition(inter)"
              >
                <div class="flex justify-between items-start">
                  <div class="flex items-center space-x-1">
                    <i :class="[getIcon(inter.type), 'text-[10px]']"></i>
                    <span class="text-[9px] font-black uppercase tracking-tighter">{{ inter.type }}</span>
                  </div>
                  <i v-if="inter.status === 'finished'" class="fa-solid fa-circle-check text-emerald-500"></i>
                  <i v-if="inter.status === 'pending' && inter.isUrgent" class="fa-solid fa-triangle-exclamation text-red-500 animate-pulse"></i>
                </div>

                <div class="mt-1">
                  <div class="text-[11px] font-bold truncate leading-tight">{{ inter.client }}</div>
                  <div class="text-[9px] opacity-70 truncate font-medium uppercase"><i class="fa-solid fa-map-pin mr-1"></i> {{ inter.city }}</div>
                </div>

                <div v-if="inter.status === 'in_progress'" class="w-full bg-black/5 h-1 rounded-full mt-1">
                  <div class="bg-current h-full rounded-full opacity-50" style="width: 60%"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="slide">
      <div v-if="selectedInter" class="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 p-6 border-l border-slate-200">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-lg font-bold text-slate-800">Détails Intervention</h3>
          <button @click="selectedInter = null" class="text-slate-400 hover:text-slate-600"><i class="fa-solid fa-xmark text-xl"></i></button>
        </div>
        
        <div class="space-y-6">
          <div class="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client</p>
            <p class="text-md font-bold text-indigo-900">{{ selectedInter.client }}</p>
            <p class="text-xs text-slate-500">{{ selectedInter.address }}</p>
          </div>

          <div>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Matériel nécessaire</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="tag in ['Fluide R32', 'Filtres G4', 'Sonde ext']" :key="tag" class="bg-indigo-50 text-indigo-600 text-[10px] px-2 py-1 rounded font-bold italic"># {{ tag }}</span>
            </div>
          </div>

          <div class="border-t pt-4">
             <button class="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition">
               <i class="fa-solid fa-phone mr-2"></i> Appeler le technicien
             </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import InterventionMapTournee from "./InterventionMapTournee.vue"

export default {
  components: { InterventionMapTournee },
  data() {
    return {
      selectedInter: null,
      hours: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
      technicians: [
        { id: 1, name: "Jean-Philippe R.", color: "#4F46E5", online: true, lastLocation: 'Lyon 3e (Rue Garibaldi)', progress: 65 },
        { id: 2, name: "Marc-Antoine D.", color: "#10B981", online: true, lastLocation: 'Villeurbanne (Totem)', progress: 40 },
        { id: 3, name: "Lucas M.", color: "#F59E0B", online: false, lastLocation: 'Bron (Entrepôt)', progress: 90 }
      ],
      addresses: [
        { id: 1, tech_id: 1, type: 'maintenance', status: 'finished', start: '08:00', duration: 90, client: 'COPRO LES CÈDRES', city: 'Lyon', address: '12 Rue des Cèdres', isUrgent: false },
        { id: 2, tech_id: 1, type: 'depannage', status: 'in_progress', start: '10:15', duration: 120, client: 'RESTO LE GOURMET', city: 'Lyon', address: '4 Quai Saint-Antoine', isUrgent: true },
        { id: 3, tech_id: 1, type: 'installation', status: 'pending', start: '14:30', duration: 180, client: 'USINE PLASTIMOD', city: 'Bourg-en-B.', address: 'Zone Ind. Nord', isUrgent: false },
        
        { id: 4, tech_id: 2, type: 'maintenance', status: 'finished', start: '08:30', duration: 120, client: 'BUREAUX NEXT-GEN', city: 'Villeurbanne', address: '88 Rue de la Doua', isUrgent: false },
        { id: 5, tech_id: 2, type: 'depannage', status: 'pending', start: '11:30', duration: 60, client: 'MME. MARTIN', city: 'Lyon', address: '15 Rue de la Paix', isUrgent: true },
      ]
    }
  },
  computed: {
    currentTimePosition() {
      // Simulation d'une position à 11h15
      const now = 11.25; // 11h15
      const start = 8;
      const total = 11; 
      return `calc(${((now - start) / total) * 100}% + 288px)`; // 288px est la largeur de la colonne tech
    },
    liveInterventionsCount() {
      return this.addresses.filter(a => a.status === 'in_progress').length;
    }
  },
  methods: {
    getInterventionsByTech(techId) {
      return this.addresses.filter(a => a.tech_id === techId);
    },
    getStatusClass(inter) {
      if (inter.status === 'finished') return 'bg-slate-100 border-slate-200 text-slate-400 grayscale opacity-60';
      if (inter.status === 'in_progress') return 'bg-indigo-50 border-indigo-500 text-indigo-700 ring-4 ring-indigo-500/10 shadow-lg';
      
      // Par défaut (pending) selon le type
      if (inter.type === 'depannage') return 'bg-red-50 border-red-500 text-red-700';
      if (inter.type === 'maintenance') return 'bg-blue-50 border-blue-500 text-blue-700';
      return 'bg-emerald-50 border-emerald-500 text-emerald-700';
    },
    getIcon(type) {
      const icons = { 'depannage': 'fa-solid fa-bolt', 'maintenance': 'fa-solid fa-screwdriver-wrench', 'installation': 'fa-solid fa-box' };
      return icons[type] || 'fa-solid fa-circle';
    },
    calculatePosition(inter) {
      const [hours, minutes] = inter.start.split(':').map(Number);
      const startInMinutes = (hours - 8) * 60 + minutes; 
      const totalDayMinutes = 11 * 60; 
      return {
        left: `${(startInMinutes / totalDayMinutes) * 100}%`,
        width: `calc(${(inter.duration / totalDayMinutes) * 100}% - 4px)`
      };
    },
    openDetails(inter) {
      this.selectedInter = inter;
    },
    optimizeRoutes() {
      // Logic here
    }
  }
}
</script>

<style scoped>
.slide-enter-active, .slide-leave-active { transition: transform 0.3s ease; }
.slide-enter, .slide-leave-to { transform: translateX(100%); }

/* Désactivation scrollbars disgracieuses */
.overflow-auto::-webkit-scrollbar { display: none; }
.overflow-auto { -ms-overflow-style: none; scrollbar-width: none; }
</style>